import React, { useCallback, useMemo, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { useChatContext } from 'features/message-center/providers';
import { useShowAlert } from 'hooks';
import API from 'services/API';
import { Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { PHONE_TYPE_MAP, PHONE_TYPES } from 'UI/components/organisms/PhoneNumbersForm/utils';
import { DEFAULT_COUNTRY_CODE, RowActions } from 'UI/constants/defaults';
import { ENTITY_PROFILES_ROUTES_MAP } from 'UI/constants/entityTypes';
import { SvgArrowDown, SvgMessageFilled, SvgPhoneFilled } from 'UI/res';
import { getErrorMessage } from 'UI/utils';

import { DISABLED_COLOR, ICON_COLOR } from './EntityListDropdown.constants';
import {
  Container,
  MenuItemLoaderContainer,
  MenuItemStyled,
  PhoneActionButton,
  PhoneActionLink,
  PhoneActionsContainer,
  useStyles
} from './EntityListDropdown.styled';
import { formatResponse } from './EntityListDropdown.utils';

export const EntityListDropdown = ({
  endpoint,
  entity,
  idKey,
  mainEntityItem,
  queryParams = {}
}) => {
  const classes = useStyles();
  const { showAlert } = useShowAlert();
  const [anchorEl, setAnchorEl] = useState(null);
  const [remainingItems, setRemainingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { openDrawer } = useChatContext();
  const canUseTextel = userHasPermission(Permissions.Textel.CanUseModule);
  const hasMultipleEntityItems = remainingItems.length > 0;
  const isOpen = Boolean(anchorEl);
  const entityRoute = entity?.id ? ENTITY_PROFILES_ROUTES_MAP[entity?.id] : null;

  const loadEntityItems = async () => {
    try {
      const mainEntityId = mainEntityItem?.[idKey];
      if (!endpoint || !idKey || !mainEntityId) return [];

      setIsLoading(true);
      const parsedQueryParams = new URLSearchParams(queryParams);
      const finalEndpoint = `${endpoint.replace(
        ':id',
        mainEntityId
      )}?${parsedQueryParams.toString()}`;
      const response = await API.get(finalEndpoint);
      const entityItems = formatResponse(response);
      return entityItems;
    } catch (error) {
      showAlert({
        body: getErrorMessage(error, strings.shared.errors.defaultMessage),
        title: strings.shared.errors.loadFail,
        severity: 'error'
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenMenu = async event => {
    setAnchorEl(event.currentTarget);
    const items = await loadEntityItems();
    setRemainingItems(items || []);
  };

  const handleCloseMenu = () => setAnchorEl(null);

  const handleOnContactActionClick = useCallback(
    (entityItem, action) => {
      if (action !== RowActions.Text) {
        handleCloseMenu();
        return;
      }

      const { phone = {} } = entityItem || {};
      const recipient = {
        entityType: entity.id,
        name: entityItem?.fullName || ''
      };

      canUseTextel &&
        recipient &&
        recipient?.entityType &&
        openDrawer({
          recipient: {
            ...recipient,
            entityType: recipient?.entityType ?? entity.id,
            phone: phone?.value || null,
            countryCode: phone?.countryCode ?? DEFAULT_COUNTRY_CODE
          }
        });

      handleCloseMenu();
    },
    [canUseTextel, entity.id, openDrawer]
  );

  const menuChildren = useMemo(() => {
    if (!isOpen) return null;
    if (isLoading) {
      return (
        <MenuItemLoaderContainer>
          <CircularProgress size={24} />
        </MenuItemLoaderContainer>
      );
    }

    if (!hasMultipleEntityItems) {
      return [
        <MenuItemStyled disableRipple key="empty">
          <Typography variant="body2">
            {strings.formatString(strings.shared.emptyState.dropdown.title, {
              entity: entity.title ?? ''
            })}
          </Typography>
        </MenuItemStyled>
      ];
    }

    return remainingItems.map(entityItem => {
      const { phone = {} } = entityItem || {};
      const countryCode = phone?.countryCode ?? DEFAULT_COUNTRY_CODE;
      const phoneNumber = phone?.value ? `${countryCode}${phone.value}` : null;
      const phoneType = phone?.typeId ? PHONE_TYPE_MAP[phone.typeId] : null;
      const isSmsEnabled =
        !!phoneNumber && phoneType ? phoneType.title !== PHONE_TYPES.Land : false;
      const link = entityRoute ? entityRoute.replace(':id', entityItem.id) : null;

      return (
        <MenuItemStyled
          disableRipple
          key={entityItem?.id}
          onClick={() => {
            link && window.open(link, '_blank', 'noopener,noreferrer');
            handleCloseMenu();
          }}
        >
          <Typography variant="body2">{entityItem?.fullName}</Typography>

          <PhoneActionsContainer>
            <Tooltip title={phoneNumber ? 'Call' : ''} placement="bottom" arrow>
              <div>
                <PhoneActionLink
                  aria-label="call"
                  aria-disabled={!phoneNumber}
                  disabled={!phoneNumber}
                  href={phoneNumber ? `tel:${phoneNumber}` : undefined}
                  onClick={event => {
                    event.stopPropagation();
                    handleOnContactActionClick(entityItem, RowActions.Call);
                  }}
                >
                  <SvgPhoneFilled fill={phoneNumber ? ICON_COLOR : DISABLED_COLOR} size={14} />
                </PhoneActionLink>
              </div>
            </Tooltip>

            <Tooltip title={isSmsEnabled ? 'Text' : ''} placement="bottom" arrow>
              <div>
                {canUseTextel ? (
                  <PhoneActionButton
                    aria-label="send text message"
                    disabled={!isSmsEnabled}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleOnContactActionClick(entityItem, RowActions.Text);
                    }}
                  >
                    <SvgMessageFilled fill={isSmsEnabled ? ICON_COLOR : DISABLED_COLOR} size={14} />
                  </PhoneActionButton>
                ) : (
                  <PhoneActionLink
                    aria-label="text"
                    aria-disabled={!isSmsEnabled}
                    disabled={!isSmsEnabled}
                    href={isSmsEnabled ? `sms:${phoneNumber}` : undefined}
                    onClick={event => {
                      event.stopPropagation();
                      if (!isSmsEnabled) event.preventDefault();
                    }}
                    tabIndex={isSmsEnabled ? 0 : -1}
                  >
                    <SvgMessageFilled fill={isSmsEnabled ? ICON_COLOR : DISABLED_COLOR} size={14} />
                  </PhoneActionLink>
                )}
              </div>
            </Tooltip>
          </PhoneActionsContainer>
        </MenuItemStyled>
      );
    });
  }, [
    isOpen,
    isLoading,
    hasMultipleEntityItems,
    remainingItems,
    entity.title,
    entityRoute,
    canUseTextel,
    handleOnContactActionClick
  ]);

  if (!mainEntityItem) return null;

  return (
    <Container>
      <Typography variant="body2">{mainEntityItem?.fullName || ''}</Typography>
      <IconButton
        aria-controls="entity-list-menu"
        aria-haspopup="true"
        className={classes.arrowButton}
        onClick={handleOpenMenu}
      >
        <SvgArrowDown size={8} />
      </IconButton>

      {isOpen && (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          PaperProps={{
            style: { maxHeight: 400, overflowY: 'auto' }
          }}
          getContentAnchorEl={null}
          id="entity-list-menu"
          onClose={handleCloseMenu}
          open={Boolean(anchorEl)}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          {menuChildren}
        </Menu>
      )}
    </Container>
  );
};
